import { Segment } from '@mairistem/composants';
import { Liste, ListeTable, ListeTableActionCsv, ListeTableActionPrint } from '@mairistem/liste';
import dayjs from 'dayjs';
import _ from 'lodash';
import React, { type ReactNode } from 'react';
import { useHistory } from 'react-router';
import { CycleContext, RegieContext } from '@/contexts';
import { baseUrlFront } from '@/routes';
import type { CycleType } from '@/types';
import { formatCyclesImpayesToCsv, getColonnesCsvCyclesImpayes, utils_liste } from '@/utils';
import CyclesListeTableHeader from './CyclesListeTableHeader';
import CyclesListeTableHeaderImpayes from './CyclesListeTableHeaderImpayes';
import CyclesListeTableRow from './CyclesListeTableRow';
import CyclesListeTableRowImpayes from './CyclesListeTableRowImpayes';

type CyclesListeProps = {
  height?: number;
  onSelect?: (selected: CycleType, selectedItems: CycleType[]) => void;
  searchable?: boolean;
  selectedItems?: CycleType[];
  /** Défini les colonnes affichées */
  type?: 'classique' | 'impayes';
};

const CyclesListe = (props: CyclesListeProps) => {
  const { height, searchable = true, onSelect, selectedItems, type = 'classique' } = props;

  const history = useHistory();

  const { regies, selectedRegie } = React.useContext(RegieContext);
  const regie = React.useMemo(
    () => _.find(regies, { identifiant: selectedRegie }),
    [regies, selectedRegie],
  );
  const { regieProlongee, joursEncaissementSupplementaires } = regie || {};

  const {
    total,
    loading,
    cycles,
    filters,
    search,
    setCycles,
    setSorts,
    setFilters,
    setSearch,
    setPage,
  } = React.useContext(CycleContext);

  /**
   * Par défaut, le clic sur un cycle renvoit vers la liste des factures
   * filtré sur le cycle cliqué
   */
  const handleSelect = React.useCallback(
    (cycle) => {
      history.push(`${baseUrlFront}/listes/factures`, { identifiantsCycles: [cycle.identifiant] });
    },
    [history],
  );

  const handleSort = React.useCallback(
    (sort) => {
      setSorts(
        _.isEmpty(sort)
          ? // tri par défaut
            null
          : _.mapValues(sort, (val) => (val === 'ascending' ? 'asc' : 'desc')),
      );
      setCycles([]);
    },
    [setCycles, setSorts],
  );

  const handleFilter = React.useCallback(
    (f) => {
      // prevent la liste de trigger une deuxième fois l'appel à l'API
      // Si on essaye de réappliquer les filtres par défaut à tord
      if (utils_liste.areListeFiltersEqual(filters, f)) {
        return;
      }

      setPage(1);
      setFilters(f);
    },
    [filters, setPage, setFilters],
  );

  const handleSearch = React.useCallback(
    async (value: string) => {
      setPage(1);
      setSearch(value);
    },
    [setPage, setSearch],
  );

  const handleLoad = React.useCallback(
    ({ page }) => {
      setPage(page);
    },
    [setPage],
  );

  /** Export du tableau des cycles impayés, avec les valeurs affichées */
  const itemsCsvImpayes = React.useMemo(
    () => (type === 'impayes' ? formatCyclesImpayesToCsv(cycles, regie) : []),
    [type, cycles, regie],
  );

  const renderRow = (item, __, rowProps) => <CyclesListeTableRow item={item} rowProps={rowProps} />;

  const renderRowImpayes = (item, invalidate, rowProps) => (
    <CyclesListeTableRowImpayes
      item={item}
      rowProps={rowProps}
      regieProlongee={regie?.regieProlongee}
      joursEncaissementSupplementaires={regie?.joursEncaissementSupplementaires}
      logicielsFacturiers={regie?.logicielsFacturier}
      invalidate={invalidate}
    />
  );

  return (
    <Segment
      basic
      style={{
        margin: 0,
        paddingTop: 0,
        height: height || '100%',
        ...(type === 'impayes' && {
          paddingTop: 0,
          paddingLeft: 0,
          paddingRight: 0,
          paddingBottom: '2em',
        }),
      }}
    >
      <Liste
        title=""
        loading={loading}
        {...(searchable && {
          onSearch: handleSearch,
          searchable: true,
          search: {
            value: search,
            placeholder: 'Rechercher un cycle de facturation...',
            clearable: true,
            autoFocus: true,
          },
        })}
      >
        {searchable && <Liste.Header> </Liste.Header>}
        <Liste.Content>
          {/* La barre de recherche et le compteur occupent déjà le haut de la liste : `height`
          ne règle que le Segment, sinon le bas du tableau est coupé et ne défile pas */}
          <div style={{ height: 'calc(100% - 100px)' }}>
            <ListeTable
              count={_.isEmpty(cycles) ? 0 : total}
              showCount
              countLabel={total > 1 ? 'cycles' : 'cycle'}
              loading={loading}
              items={_.map(cycles, (cycle) => ({
                ...cycle,
                dateLimiteProlongee: regieProlongee
                  ? dayjs(cycle.dateLimite)
                      .add(joursEncaissementSupplementaires, 'day')
                      .format('YYYY-MM-DD')
                  : null,
              }))}
              onSort={handleSort}
              // Les cycles impayés sont tous chargés (allAtOnce) pour être exportés
              perPage={type === 'impayes' ? 500 : 30}
              onLoad={handleLoad}
              onFilter={handleFilter}
              onSelect={handleSelect}
              {...(!_.isNil(onSelect) && {
                onSelect,
                selectedItems,
                selection: 'checkbox',
              })}
            >
              <ListeTable.Header>
                {type === 'impayes' ? (
                  <CyclesListeTableHeaderImpayes
                    regieProlongee={regie?.regieProlongee}
                    // Burger rendu à côté du libellé « Edition » : passé à ListeTable, il
                    // flotterait en haut de l'en-tête sur deux lignes, au-dessus du libellé
                    actions={
                      <ListeTable.Actions>
                        <ListeTableActionCsv
                          items={itemsCsvImpayes}
                          columns={getColonnesCsvCyclesImpayes(regie)}
                          options={{ filename: 'Cycles de facturation impayés' }}
                        />
                        <ListeTableActionPrint
                          printOptions={{
                            documentName: 'Cycles de facturation impayés',
                            // La colonne des icônes PDF n'a pas de sens à l'impression
                            pageStyle: '[data-name="edition"] { display: none !important; }',
                          }}
                        />
                      </ListeTable.Actions>
                    }
                  />
                ) : (
                  <CyclesListeTableHeader />
                )}
              </ListeTable.Header>
              {type === 'impayes'
                ? (renderRowImpayes as unknown as ReactNode)
                : (renderRow as unknown as ReactNode)}
            </ListeTable>
          </div>
        </Liste.Content>
      </Liste>
    </Segment>
  );
};

export default CyclesListe;
